import { TenantTypes } from '@tenants/manager';
import {
  AgentSearchConfig,
  ContactUsConfig,
  FiltersConfig,
  FooterConfig,
  HomeConfig,
  JoinExpConfig,
  SEOConfig,
  TENANT_CONFIG_AGENT_SEARCH_KEY,
  TENANT_CONFIG_CONTACT_US_KEY,
  TENANT_CONFIG_FILTERS_KEY,
  TENANT_CONFIG_FOOTER_KEY,
  TENANT_CONFIG_HOME_KEY,
  TENANT_CONFIG_JOIN_EXP_KEY,
  TENANT_CONFIG_SEO_KEY,
} from '@tenants/types';

/**
 * Utility function to get the {@link SEOConfig SEO config} for a tenant if it exists.
 *
 * @param tenant The tenant to get the SEO config for
 * @returns The SEO config for the tenant or undefined if it doesn't exist
 */
export const getTenantSeoConfig = (tenant: TenantTypes): SEOConfig | undefined => {
  const seoConfig = TENANT_CONFIG_SEO_KEY in tenant.config ? tenant.config.seo as SEOConfig : undefined;
  return seoConfig;
};

/**
 * Utility function to get the {@link HomeConfig home config} for a tenant if it exists.
 *
 * @param tenant The tenant to get the home config for
 * @returns The home config for the tenant or undefined if it doesn't exist
 */
export const getTenantHomeConfig = (tenant: TenantTypes): HomeConfig | undefined => {
  const homeConfig = TENANT_CONFIG_HOME_KEY in tenant.config ? tenant.config.home as HomeConfig : undefined;
  return homeConfig;
};

/**
 * Utility function to get the {@link FooterConfig footer config} for a tenant if it exists.
 *
 * @param tenant The tenant to get the footer config for
 * @returns The footer config for the tenant or undefined if it doesn't exist
 */
export const getTenantFooterConfig = (tenant: TenantTypes): FooterConfig | undefined => {
  const footerConfig = TENANT_CONFIG_FOOTER_KEY in tenant.config ? tenant.config.footer as FooterConfig : undefined;
  return footerConfig;
};

/**
 * Utility function to get the {@link FiltersConfig filters config} for a tenant if it exists.
 *
 * @param tenant The tenant to get the filters config for
 * @returns The filters config for the tenant or undefined if it doesn't exist
 */
export const getTenantFiltersConfig = (tenant: TenantTypes): FiltersConfig | undefined => {
  const filtersConfig = TENANT_CONFIG_FILTERS_KEY in tenant.config ? tenant.config.filters as FiltersConfig : undefined;
  return filtersConfig;
};

/**
 * Utility function to get the {@link AgentSearchConfig agent search config} for a tenant if it exists.
 *
 * @param tenant The tenant to get the agent search config for
 * @returns The agent search config for the tenant or undefined if it doesn't exist
 */
export const getTenantAgentSearchConfig = (tenant: TenantTypes): AgentSearchConfig | undefined => {
  const agentSearchConfig = TENANT_CONFIG_AGENT_SEARCH_KEY in tenant.config ? tenant.config.agentSearch as AgentSearchConfig : undefined;
  return agentSearchConfig;
};

/**
 * Utility function to get the {@link JoinExpConfig join exp config} for a tenant if it exists.
 *
 * @param tenant The tenant to get the join exp config for
 * @returns The join exp config for the tenant or undefined if it doesn't exist
 */
export const getJoinExpConfig = (tenant: TenantTypes): JoinExpConfig | undefined => {
  const joinExpConfig = TENANT_CONFIG_JOIN_EXP_KEY in tenant.config ? tenant.config.joinExp as JoinExpConfig : undefined;
  return joinExpConfig;
};

/**
 * Utility function to get the {@link ContactUsConfig contact us config} for a tenant if it exists.
 *
 * @param tenant The tenant to get the contact us config for
 * @returns The contact us config for the tenant or undefined if it doesn't exist
 */
export const getTenantContactUsConfig = (tenant: TenantTypes): ContactUsConfig | undefined => {
  const contactUsConfig = TENANT_CONFIG_CONTACT_US_KEY in tenant.config ? tenant.config.contactUs as ContactUsConfig : undefined;
  return contactUsConfig;
};